import {
  floorToDecimals,
  gtUsdt,
  isPositiveUsdt,
  tryM,
} from "./money";

/** 提现弹窗金额：小数位上限（与余额 floor 到 4 位一致） */
export const WITHDRAW_AMOUNT_MAX_DECIMALS = 4;

function floorUsdtAmountToInputString(balance: number): string {
  if (!Number.isFinite(balance) || balance <= 0) return "";
  // Decimal-safe: ROUND_DOWN to 4 decimals, then strip trailing zeros so the
  // input field shows the user-friendly form (e.g. "100.5678" not "100.56780000").
  return floorToDecimals(balance, WITHDRAW_AMOUNT_MAX_DECIMALS).replace(
    /\.?0+$/,
    "",
  );
}

/**
 * 受控输入清洗：仅保留数字与单小数点，小数最多四位，数值超过可用余额时钳制为可提现上限。
 * 允许中间态（如 "0."、"12."），由 `isValidWithdrawAmountForSubmit` 控制确认按钮。
 */
export function sanitizeWithdrawAmountInput(raw: string, balance: number): string {
  if (raw === "") return "";

  let s = raw.replace(/[^\d.]/g, "");
  const dotIdx = s.indexOf(".");
  if (dotIdx !== -1) {
    s = s.slice(0, dotIdx + 1) + s.slice(dotIdx + 1).replace(/\./g, "");
  }

  if (s.startsWith(".")) {
    s = `0${s}`;
  }

  const parts = s.split(".");
  const intPart = parts[0] ?? "";
  const fracRaw = parts.length > 1 ? parts[1] : undefined;
  if (fracRaw !== undefined) {
    s = `${intPart}.${fracRaw.slice(0, WITHDRAW_AMOUNT_MAX_DECIMALS)}`;
  } else {
    s = intPart;
  }

  if (s === "") return "";

  const endsWithDot = s.endsWith(".");
  const numericBody = endsWithDot ? s.slice(0, -1) : s;
  if (numericBody === "") return s;

  // Decimal-safe comparison: avoids 0.1 + 0.2 style drift on amount > balance,
  // and preserves precision for backend balances exceeding 2^53.
  const parsedBalance = tryM(balance);
  if (parsedBalance !== null && gtUsdt(numericBody, parsedBalance)) {
    return floorUsdtAmountToInputString(balance);
  }

  return s;
}

/** 合法正数、≤ 余额、小数位 ≤4、无尾点等未完成形态 */
export function isValidWithdrawAmountForSubmit(amount: string, balance: number): boolean {
  if (!amount || !Number.isFinite(balance)) return false;
  if (amount.endsWith(".")) return false;
  if (!/^\d+(\.\d+)?$/.test(amount)) return false;

  const frac = amount.includes(".") ? amount.split(".")[1] ?? "" : "";
  if (frac.length > WITHDRAW_AMOUNT_MAX_DECIMALS) return false;

  if (!isPositiveUsdt(amount)) return false;
  if (gtUsdt(amount, balance)) return false;

  return true;
}
